import React from 'react';

import { Pagination as MuiPagination, PaginationProps as MuiPaginationProps, PaginationItem } from '@mui/material';

import styles from './Pagination.module.scss';

export interface PaginationProps extends Omit<MuiPaginationProps, 'variant' | 'shape'> {
    /**
     * The total number of pages
     */
    count: number;
    /**
     * The current page number (1-indexed)
     */
    page?: number;
    /**
     * Callback fired when the page is changed
     */
    onChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
    /**
     * Whether the pagination is disabled
     */
    disabled?: boolean;
}

/**
 * Custom Pagination component based on MUI Pagination
 * Implements design system styling from Figma table Pagination component
 * Supports light and dark themes via CSS variables
 * Features: Previous/Next buttons, page numbers, selected state, disabled state
 */
export const Pagination: React.FC<PaginationProps> = ({ count, page = 1, onChange, disabled, ...props }) => {
    return (
        <div className={styles.paginationContainer}>
            <MuiPagination
                count={count}
                page={page}
                onChange={onChange}
                disabled={disabled}
                showFirstButton={false}
                showLastButton={false}
                siblingCount={1}
                boundaryCount={1}
                classes={{
                    root: styles.pagination,
                    ul: styles.paginationList,
                }}
                renderItem={(item) => (
                    <PaginationItem
                        {...item}
                        slots={{
                            previous: () => (
                                <div className={styles.navigationButton}>
                                    <svg
                                        width={'16'}
                                        height={'16'}
                                        viewBox={'0 0 16 16'}
                                        fill={'none'}
                                        className={styles.icon}
                                    >
                                        <path
                                            d={'M10 12L6 8L10 4'}
                                            stroke={'currentColor'}
                                            strokeWidth={'1.5'}
                                            strokeLinecap={'round'}
                                            strokeLinejoin={'round'}
                                        />
                                    </svg>
                                    <span>Previous</span>
                                </div>
                            ),
                            next: () => (
                                <div className={styles.navigationButton}>
                                    <span>Next</span>
                                    <svg
                                        width={'16'}
                                        height={'16'}
                                        viewBox={'0 0 16 16'}
                                        fill={'none'}
                                        className={styles.icon}
                                    >
                                        <path
                                            d={'M6 4L10 8L6 12'}
                                            stroke={'currentColor'}
                                            strokeWidth={'1.5'}
                                            strokeLinecap={'round'}
                                            strokeLinejoin={'round'}
                                        />
                                    </svg>
                                </div>
                            ),
                        }}
                        className={`${styles.paginationItem} ${
                            item.type === 'previous' || item.type === 'next' ? styles.navItem : ''
                        } ${item.selected ? styles.selected : ''}`}
                    />
                )}
                {...props}
            />
        </div>
    );
};

export default Pagination;
